import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Alert,
  CircularProgress,
  IconButton,
  Chip,
  FormControl,
  InputLabel,
  Select,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
  Checkbox,
  CardActions,
  Grid,
  InputAdornment,
  Collapse
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CreditCard as LoanIcon,
  Home as HomeIcon,
  Business as BusinessIcon,
  Person as PersonalIcon,
  Refresh as RefreshIcon,
  ViewModule as ViewModuleIcon,
  ViewList as ViewListIcon,
  FilterList as FilterIcon,
  Search as SearchIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { api } from '../contexts/ApiContext';
import {
  FIT_TABLE_SX,
  FIT_TABLE_CONTAINER_SX,
  FitColgroup,
  buildFitColgroup,
  fitTruncateSx,
  fitChipSx,
  fitHeaderLabel,
} from '../utils/fitTable';

interface Property {
  id: number;
  nickname: string;
}

interface Loan {
  id: number;
  property_id: number | null;
  loan_name: string;
  lender: string;
  original_amount: number;
  current_balance: number;
  interest_rate: number;
  monthly_payment: number;
  loan_type: string;
  start_date: string;
  end_date: string;
  is_tracker?: boolean;
  marginal_rate?: number | null;
  created_at: string;
  updated_at: string;
  property?: Property;
}

export const Loans: React.FC = () => {
  const [loans, setLoans] = useState<Loan[]>([]);
  const [properties, setProperties] = useState<Property[]>([]);
  const [currentECBRate, setCurrentECBRate] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedLoan, setSelectedLoan] = useState<Loan | null>(null);
  const [viewMode, setViewMode] = useState<'table' | 'tile'>('table');
  const [selectedLoans, setSelectedLoans] = useState<number[]>([]);
  const [showFilters, setShowFilters] = useState(false);
  
  // Filter states
  const [loanTypeFilter, setLoanTypeFilter] = useState<string>('');
  const [propertyFilter, setPropertyFilter] = useState<string>('');
  const [lenderFilter, setLenderFilter] = useState<string>('');
  const [loanNameFilter, setLoanNameFilter] = useState<string>('');
  
  const [formData, setFormData] = useState({
    loan_name: '',
    lender: '',
    original_amount: '',
    current_balance: '',
    interest_rate: '',
    monthly_payment: '',
    loan_type: 'personal',
    start_date: '',
    end_date: '',
    is_tracker: false,
    marginal_rate: ''
  });

  const fetchLoans = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await api.get('/api/loans/');
      setLoans(response.data.loans || []);
    } catch (err: any) {
      console.error('Error fetching loans:', err);
      setError(err.response?.data?.error || 'Failed to load loans');
    } finally {
      setLoading(false);
    }
  };

  const fetchProperties = async () => {
    try {
      const response = await api.get('/api/properties/');
      setProperties(response.data.properties || []);
    } catch (err: any) {
      console.error('Error fetching properties:', err);
    }
  };

  const fetchCurrentECBRate = async () => {
    try {
      const response = await api.get('/api/ecb-rates/current');
      if (response.data && response.data.ecb_rate) {
        setCurrentECBRate(response.data.ecb_rate.main_refinancing_rate);
      }
    } catch (err: any) {
      console.error('Error fetching current ECB rate:', err);
      // Continue without ECB rate - will use fallback
    }
  };

  useEffect(() => {
    fetchLoans();
    fetchProperties();
    fetchCurrentECBRate();
  }, []);

  // Calculate interest rate for today
  const getInterestRateToday = (loan: Loan): number | null => {
    if (loan.is_tracker && loan.marginal_rate !== null && loan.marginal_rate !== undefined) {
      // For tracker loans: ECB rate + marginal rate
      if (currentECBRate !== null) {
        return currentECBRate + loan.marginal_rate;
      }
      // Fallback: use stored interest_rate if ECB rate not available
      return loan.interest_rate;
    } else {
      // For fixed loans: use stored interest_rate
      return loan.interest_rate;
    }
  };

  // Filter loans based on filter states
  const filteredLoans = loans.filter(loan => {
    // Loan type filter
    if (loanTypeFilter && loan.loan_type !== loanTypeFilter) {
      return false;
    }
    
    // Property filter
    if (propertyFilter) {
      if (propertyFilter === 'none' && loan.property_id !== null) {
        return false;
      }
      if (propertyFilter !== 'none' && loan.property_id?.toString() !== propertyFilter) {
        return false;
      }
    }
    
    // Lender filter (case-insensitive search)
    if (lenderFilter && !loan.lender.toLowerCase().includes(lenderFilter.toLowerCase())) {
      return false;
    }
    
    // Loan name filter (case-insensitive search)
    if (loanNameFilter && !loan.loan_name.toLowerCase().includes(loanNameFilter.toLowerCase())) {
      return false;
    }
    
    return true;
  });

  const handleOpenDialog = (loan?: Loan) => {
    if (loan) {
      setSelectedLoan(loan);
      setFormData({
        loan_name: loan.loan_name,
        lender: loan.lender,
        original_amount: loan.original_amount.toString(),
        current_balance: loan.current_balance.toString(),
        interest_rate: loan.interest_rate.toString(),
        monthly_payment: loan.monthly_payment.toString(),
        loan_type: loan.loan_type,
        start_date: loan.start_date,
        end_date: loan.end_date,
        is_tracker: loan.is_tracker || false,
        marginal_rate: loan.marginal_rate?.toString() || ''
      });
    } else {
      setSelectedLoan(null);
      setFormData({
        loan_name: '',
        lender: '',
        original_amount: '',
        current_balance: '',
        interest_rate: '',
        monthly_payment: '',
        loan_type: 'personal',
        start_date: '',
        end_date: '',
        is_tracker: false,
        marginal_rate: ''
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedLoan(null);
    setError('');
  };

  const handleOpenDeleteDialog = (loan: Loan) => {
    setSelectedLoan(loan);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setSelectedLoan(null);
  };

  const handleSubmit = async () => {
    setError('');
    try {
      const payload: any = {
        loan_name: formData.loan_name,
        lender: formData.lender,
        original_amount: parseFloat(formData.original_amount) || 0,
        current_balance: parseFloat(formData.current_balance) || 0,
        monthly_payment: parseFloat(formData.monthly_payment) || 0,
        loan_type: formData.loan_type,
        start_date: formData.start_date,
        end_date: formData.end_date,
        is_tracker: formData.is_tracker
      };

      // For tracker loans, use marginal_rate; for fixed loans, use interest_rate
      if (formData.is_tracker) {
        payload.marginal_rate = parseFloat(formData.marginal_rate) || 0;
        // For tracker loans, we still need to store interest_rate for backward compatibility
        // Calculate it based on current ECB rate + marginal rate
        if (currentECBRate !== null) {
          payload.interest_rate = currentECBRate + (parseFloat(formData.marginal_rate) || 0);
        } else {
          payload.interest_rate = parseFloat(formData.interest_rate) || 0;
        }
      } else {
        payload.interest_rate = parseFloat(formData.interest_rate) || 0;
        payload.marginal_rate = null;
      }

      if (selectedLoan) {
        await api.put(`/api/loans/${selectedLoan.id}`, payload);
      } else {
        await api.post('/api/loans/', payload);
      }
      
      handleCloseDialog();
      fetchLoans();
    } catch (err: any) {
      console.error('Failed to save loan:', err);
      setError(err.response?.data?.error || 'Failed to save loan');
    }
  };

  const handleDelete = async () => {
    if (selectedLoan) {
      try {
        await api.delete(`/api/loans/${selectedLoan.id}`);
        handleCloseDeleteDialog();
        fetchLoans();
      } catch (err: any) {
        console.error('Failed to delete loan:', err);
        setError(err.response?.data?.error || 'Failed to delete loan');
      }
    }
  };

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelectedLoans(loans.map(l => l.id));
    } else {
      setSelectedLoans([]);
    }
  };

  const handleSelectOne = (loanId: number) => {
    setSelectedLoans(prev => {
      if (prev.includes(loanId)) {
        return prev.filter(id => id !== loanId);
      } else {
        return [...prev, loanId];
      }
    });
  };

  const handleBulkDelete = async () => {
    if (selectedLoans.length === 0) return;
    
    if (!window.confirm(`Are you sure you want to delete ${selectedLoans.length} selected loans?`)) {
      return;
    }

    try {
      setError('');
      const deletePromises = selectedLoans.map(loanId => 
        api.delete(`/api/loans/${loanId}`)
      );
      
      await Promise.all(deletePromises);
      alert(`Successfully deleted ${selectedLoans.length} loans`);
      setSelectedLoans([]);
      fetchLoans();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete some loans');
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const formatPercentage = (rate: number) => {
    // Interest rates should be stored as percentages (e.g., 2.5 for 2.5%)
    // However, some legacy data might be stored as decimals (0.0001 = 0.01%)
    // If rate is less than 1 and greater than 0, it's likely stored as decimal
    if (rate < 1 && rate > 0) {
      // Likely stored as decimal (0.0001), convert to percentage
      return `${(rate * 100).toFixed(2)}%`;
    }
    // Already a percentage (or zero), show 2 decimal places
    return `${rate.toFixed(2)}%`;
  };

  const formatMarginalRate = (rate: number) => {
    // Marginal rates are always stored as percentages (e.g., 0.85 for 0.85%)
    // Display directly with 2 decimal places
    return `${rate.toFixed(2)}%`;
  };

  const getLoanTypeIcon = (type: string) => {
    switch (type) {
      case 'mortgage': return <HomeIcon />;
      case 'business': return <BusinessIcon />;
      case 'personal': return <PersonalIcon />;
      default: return <LoanIcon />;
    }
  };

  const getLoanTypeColor = (type: string) => {
    switch (type) {
      case 'mortgage': return 'primary';
      case 'business': return 'secondary';
      case 'personal': return 'success';
      default: return 'default';
    }
  };

  const calculateRemainingPayments = (loan: Loan) => {
    if (loan.monthly_payment <= 0) return 'N/A';
    const remaining = loan.current_balance / loan.monthly_payment;
    return Math.ceil(remaining).toString();
  };

  if (loading) {
    return (
      <Container maxWidth="xl">
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl">
      <Box sx={{ my: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" component="h1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            Loans 💳
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <Button
              variant="outlined"
              startIcon={<FilterIcon />}
              onClick={() => setShowFilters(!showFilters)}
              size="small"
              color="primary"
            >
              {showFilters ? 'Hide Filters' : 'Show Filters'}
            </Button>
            {selectedLoans.length > 0 && viewMode === 'table' && (
              <Button
                variant="contained"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={handleBulkDelete}
              >
                Delete Selected ({selectedLoans.length})
              </Button>
            )}
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={(e, newMode) => newMode && setViewMode(newMode)}
              size="small"
            >
              <ToggleButton value="table">
                <ViewListIcon />
              </ToggleButton>
              <ToggleButton value="tile">
                <ViewModuleIcon />
              </ToggleButton>
            </ToggleButtonGroup>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={fetchLoans}
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
            >
              Add Loan
            </Button>
            <Button
              variant="outlined"
              component="label"
              startIcon={<LoanIcon />}
            >
              Import Loans
              <input
                type="file"
                accept=".csv,.xlsx,.xls"
                style={{ display: 'none' }}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    console.log('Loans import file selected:', file.name);
                    // TODO: Implement loans import logic
                  }
                }}
              />
            </Button>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Active Filters Chips */}
        {(loanNameFilter || lenderFilter || loanTypeFilter || propertyFilter) && (
          <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
              Active filters:
            </Typography>
            {loanNameFilter && (
              <Chip
                label={`Loan Name: ${loanNameFilter}`}
                onDelete={() => setLoanNameFilter('')}
                deleteIcon={<CloseIcon />}
                size="small"
                color="primary"
                variant="outlined"
              />
            )}
            {lenderFilter && (
              <Chip
                label={`Lender: ${lenderFilter}`}
                onDelete={() => setLenderFilter('')}
                deleteIcon={<CloseIcon />}
                size="small"
                color="primary"
                variant="outlined"
              />
            )}
            {loanTypeFilter && (
              <Chip
                label={`Type: ${loanTypeFilter.charAt(0).toUpperCase() + loanTypeFilter.slice(1)}`}
                onDelete={() => setLoanTypeFilter('')}
                deleteIcon={<CloseIcon />}
                size="small"
                color="primary"
                variant="outlined"
              />
            )}
            {propertyFilter && (
              <Chip
                label={propertyFilter === 'none' ? 'Property: No Property' : `Property: ${properties.find(p => p.id.toString() === propertyFilter)?.nickname || propertyFilter}`}
                onDelete={() => setPropertyFilter('')}
                deleteIcon={<CloseIcon />}
                size="small"
                color="primary"
                variant="outlined"
              />
            )}
            <Typography variant="caption" sx={{ ml: 'auto', color: 'text.secondary' }}>
              Showing {filteredLoans.length} of {loans.length} loans
            </Typography>
          </Box>
        )}

        {/* Filters Section */}
        <Collapse in={showFilters}>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Filters
              </Typography>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Search Loan Name"
                    placeholder="Loan name..."
                    value={loanNameFilter}
                    onChange={(e) => setLoanNameFilter(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Search Lender"
                    placeholder="Lender name..."
                    value={lenderFilter}
                    onChange={(e) => setLenderFilter(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Loan Type</InputLabel>
                    <Select
                      value={loanTypeFilter}
                      label="Loan Type"
                      onChange={(e) => setLoanTypeFilter(e.target.value)}
                    >
                      <MenuItem value="">All Types</MenuItem>
                      <MenuItem value="personal">Personal</MenuItem>
                      <MenuItem value="business">Business</MenuItem>
                      <MenuItem value="mortgage">Mortgage</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Property</InputLabel>
                    <Select
                      value={propertyFilter}
                      label="Property"
                      onChange={(e) => setPropertyFilter(e.target.value)}
                    >
                      <MenuItem value="">All Properties</MenuItem>
                      <MenuItem value="none">No Property</MenuItem>
                      {properties.map((property) => (
                        <MenuItem key={property.id} value={property.id.toString()}>
                          {property.nickname || `Property ${property.id}`}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
              {(loanTypeFilter || propertyFilter || lenderFilter || loanNameFilter) && (
                <Box sx={{ mt: 2 }}>
                  <Button
                    size="small"
                    onClick={() => {
                      setLoanTypeFilter('');
                      setPropertyFilter('');
                      setLenderFilter('');
                      setLoanNameFilter('');
                    }}
                  >
                    Clear All Filters
                  </Button>
                </Box>
              )}
            </CardContent>
          </Card>
        </Collapse>

        {loans.length === 0 ? (
          <Card>
            <CardContent>
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <LoanIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No loans yet.
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Add your first loan to get started.
                </Typography>
              </Box>
            </CardContent>
          </Card>
        ) : viewMode === 'table' ? (
          <TableContainer component={Paper} sx={FIT_TABLE_CONTAINER_SX}>
                <Table size="small" sx={FIT_TABLE_SX}>
                  <FitColgroup
                    cols={buildFitColgroup([
                      { key: 'select', pct: 3 },
                      { key: 'loanName' },
                      { key: 'property', pct: 10 },
                      { key: 'lender', pct: 10 },
                      { key: 'type', pct: 8 },
                      { key: 'originalAmount', pct: 8 },
                      { key: 'currentBalance', pct: 8 },
                      { key: 'tracker', pct: 6 },
                      { key: 'marginalRate', pct: 8 },
                      { key: 'rateToday', pct: 9 },
                      { key: 'monthlyPayment', pct: 8 },
                      { key: 'actions', pct: 6 },
                    ])}
                  />
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedLoans.length === filteredLoans.length && filteredLoans.length > 0}
                          indeterminate={selectedLoans.length > 0 && selectedLoans.length < filteredLoans.length}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedLoans(filteredLoans.map(l => l.id));
                            } else {
                              setSelectedLoans([]);
                            }
                          }}
                        />
                      </TableCell>
                      <TableCell>{fitHeaderLabel('Loan\nName')}</TableCell>
                      <TableCell>Property</TableCell>
                      <TableCell>Lender</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell align="right">{fitHeaderLabel('Original\nAmount')}</TableCell>
                      <TableCell align="right">{fitHeaderLabel('Current\nBalance')}</TableCell>
                      <TableCell align="center">{fitHeaderLabel('Tracker\nLoan')}</TableCell>
                      <TableCell align="right">{fitHeaderLabel('Marginal Rate\nAbove ECB')}</TableCell>
                      <TableCell align="right">{fitHeaderLabel('Interest Rate\n(Today)')}</TableCell>
                      <TableCell align="right">{fitHeaderLabel('Monthly\nPayment')}</TableCell>
                      <TableCell align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredLoans.map((loan) => (
                      <TableRow 
                        key={loan.id} 
                        hover
                        selected={selectedLoans.includes(loan.id)}
                      >
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={selectedLoans.includes(loan.id)}
                            onChange={() => handleSelectOne(loan.id)}
                          />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                            <Box component="span" sx={{ display: 'flex', flexShrink: 0 }}>
                              {getLoanTypeIcon(loan.loan_type)}
                            </Box>
                            <Typography variant="body2" fontWeight="medium" sx={fitTruncateSx} title={loan.loan_name}>
                              {loan.loan_name}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          {loan.property ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                              <HomeIcon color="primary" fontSize="small" sx={{ flexShrink: 0 }} />
                              <Typography variant="body2" sx={fitTruncateSx} title={loan.property.nickname}>
                                {loan.property.nickname}
                              </Typography>
                            </Box>
                          ) : (
                            <Typography variant="body2" color="text.secondary" sx={fitTruncateSx}>
                              No property
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={fitTruncateSx} title={loan.lender}>
                            {loan.lender}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={loan.loan_type.charAt(0).toUpperCase() + loan.loan_type.slice(1)}
                            size="small"
                            color={getLoanTypeColor(loan.loan_type) as any}
                            icon={getLoanTypeIcon(loan.loan_type)}
                            sx={fitChipSx}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2">
                            {formatCurrency(loan.original_amount)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight="medium">
                            {formatCurrency(loan.current_balance)}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={loan.is_tracker ? 'Yes' : 'No'}
                            size="small"
                            color={loan.is_tracker ? 'primary' : 'default'}
                            sx={fitChipSx}
                          />
                        </TableCell>
                        <TableCell align="right">
                          {loan.is_tracker && loan.marginal_rate !== null && loan.marginal_rate !== undefined ? (
                            <Typography variant="body2">
                              {formatMarginalRate(loan.marginal_rate)}
                            </Typography>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              N/A
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="right">
                          {(() => {
                            const rateToday = getInterestRateToday(loan);
                            return rateToday !== null ? (
                              <Box>
                                <Typography variant="body2">
                                  {formatPercentage(rateToday)}
                                </Typography>
                                {loan.is_tracker && currentECBRate !== null && (
                                  <Typography variant="caption" color="text.secondary" display="block">
                                    (ECB {formatPercentage(currentECBRate)} + {formatMarginalRate(loan.marginal_rate || 0)})
                                  </Typography>
                                )}
                              </Box>
                            ) : (
                              <Typography variant="body2" color="text.secondary">
                                N/A
                              </Typography>
                            );
                          })()}
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2">
                            {formatCurrency(loan.monthly_payment)}
                          </Typography>
                        </TableCell>
                        <TableCell align="center" className="fit-table-no-clip">
                          <Box sx={{ display: 'flex', gap: 0.25, justifyContent: 'center' }}>
                            <Tooltip title="Edit Loan">
                              <IconButton
                                size="small"
                                onClick={() => handleOpenDialog(loan)}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete Loan">
                              <IconButton
                                size="small"
                                onClick={() => handleOpenDeleteDialog(loan)}
                                color="error"
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
        ) : (
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 2 }}>
            {filteredLoans.map((loan) => (
              <Card key={loan.id} sx={{ height: '100%' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {getLoanTypeIcon(loan.loan_type)}
                      <Typography variant="h6" component="h3">
                        {loan.loan_name}
                      </Typography>
                    </Box>
                    <Checkbox
                      checked={selectedLoans.includes(loan.id)}
                      onChange={() => handleSelectOne(loan.id)}
                    />
                  </Box>
                  
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Lender
                    </Typography>
                    <Typography variant="body1">
                      {loan.lender}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Property
                    </Typography>
                    {loan.property ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <HomeIcon color="primary" fontSize="small" />
                        <Typography variant="body1">
                          {loan.property.nickname}
                        </Typography>
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No property
                      </Typography>
                    )}
                  </Box>
                  
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Type
                    </Typography>
                    <Chip
                      label={loan.loan_type.charAt(0).toUpperCase() + loan.loan_type.slice(1)}
                      size="small"
                      color={getLoanTypeColor(loan.loan_type) as any}
                      icon={getLoanTypeIcon(loan.loan_type)}
                    />
                  </Box>
                  
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Original Amount
                    </Typography>
                    <Typography variant="h6" color="primary">
                      {formatCurrency(loan.original_amount)}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Current Balance
                    </Typography>
                    <Typography variant="body1">
                      {formatCurrency(loan.current_balance)}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Tracker Loan
                    </Typography>
                    <Chip
                      label={loan.is_tracker ? 'Yes' : 'No'}
                      size="small"
                      color={loan.is_tracker ? 'primary' : 'default'}
                    />
                  </Box>
                  
                  {loan.is_tracker && loan.marginal_rate !== null && loan.marginal_rate !== undefined && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Marginal Rate Above ECB
                      </Typography>
                      <Typography variant="body1">
                        {formatMarginalRate(loan.marginal_rate)}
                      </Typography>
                    </Box>
                  )}
                  
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Interest Rate (Today)
                    </Typography>
                    {(() => {
                      const rateToday = getInterestRateToday(loan);
                      return rateToday !== null ? (
                        <Box>
                          <Typography variant="body1">
                            {formatPercentage(rateToday)}
                          </Typography>
                          {loan.is_tracker && currentECBRate !== null && (
                            <Typography variant="caption" color="text.secondary">
                              ECB {formatPercentage(currentECBRate)} + {formatMarginalRate(loan.marginal_rate || 0)}
                            </Typography>
                          )}
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          N/A
                        </Typography>
                      );
                    })()}
                  </Box>
                  
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Monthly Payment
                    </Typography>
                    <Typography variant="body1">
                      {formatCurrency(loan.monthly_payment)}
                    </Typography>
                  </Box>
                </CardContent>
                <CardActions sx={{ justifyContent: 'flex-end', pt: 0 }}>
                  <Tooltip title="Edit Loan">
                    <IconButton
                      size="small"
                      onClick={() => handleOpenDialog(loan)}
                    >
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete Loan">
                    <IconButton
                      size="small"
                      onClick={() => handleOpenDeleteDialog(loan)}
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </CardActions>
              </Card>
            ))}
          </Box>
        )}

        {/* Add/Edit Loan Dialog */}
        <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
          <DialogTitle>
            {selectedLoan ? 'Edit Loan' : 'Add New Loan'}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2 }}>
              <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <TextField
                  fullWidth
                  label="Loan Name"
                  value={formData.loan_name}
                  onChange={(e) => setFormData({ ...formData, loan_name: e.target.value })}
                  required
                />
                <TextField
                  fullWidth
                  label="Lender"
                  value={formData.lender}
                  onChange={(e) => setFormData({ ...formData, lender: e.target.value })}
                  required
                />
              </Box>
              
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Loan Type</InputLabel>
                <Select
                  value={formData.loan_type}
                  label="Loan Type"
                  onChange={(e) => setFormData({ ...formData, loan_type: e.target.value })}
                >
                  <MenuItem value="personal">Personal</MenuItem>
                  <MenuItem value="business">Business</MenuItem>
                  <MenuItem value="mortgage">Mortgage</MenuItem>
                </Select>
              </FormControl>

              {formData.loan_type === 'mortgage' && (
                <Box sx={{ mb: 2 }}>
                  <FormControl fullWidth>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Checkbox
                        checked={formData.is_tracker}
                        onChange={(e) => setFormData({ ...formData, is_tracker: e.target.checked })}
                      />
                      <Typography>Tracker Loan (ECB + Marginal Rate)</Typography>
                    </Box>
                  </FormControl>
                </Box>
              )}

              <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <TextField
                  fullWidth
                  label="Original Amount"
                  type="number"
                  value={formData.original_amount}
                  onChange={(e) => setFormData({ ...formData, original_amount: e.target.value })}
                  inputProps={{ step: "0.01" }}
                  required
                />
                <TextField
                  fullWidth
                  label="Current Balance"
                  type="number"
                  value={formData.current_balance}
                  onChange={(e) => setFormData({ ...formData, current_balance: e.target.value })}
                  inputProps={{ step: "0.01" }}
                  required
                />
              </Box>

              <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                {formData.is_tracker ? (
                  <TextField
                    fullWidth
                    label="Marginal Rate Above ECB (%)"
                    type="number"
                    value={formData.marginal_rate}
                    onChange={(e) => setFormData({ ...formData, marginal_rate: e.target.value })}
                    inputProps={{ step: "0.01" }}
                    helperText={currentECBRate !== null ? `Current effective rate: ${(currentECBRate + (parseFloat(formData.marginal_rate) || 0)).toFixed(2)}% (ECB ${currentECBRate.toFixed(2)}% + ${(parseFloat(formData.marginal_rate) || 0).toFixed(2)}%)` : 'ECB rate not available'}
                    required
                  />
                ) : (
                  <TextField
                    fullWidth
                    label="Interest Rate (%)"
                    type="number"
                    value={formData.interest_rate}
                    onChange={(e) => setFormData({ ...formData, interest_rate: e.target.value })}
                    inputProps={{ step: "0.01" }}
                    required
                  />
                )}
                <TextField
                  fullWidth
                  label="Monthly Payment"
                  type="number"
                  value={formData.monthly_payment}
                  onChange={(e) => setFormData({ ...formData, monthly_payment: e.target.value })}
                  inputProps={{ step: "0.01" }}
                  required
                />
              </Box>

              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  fullWidth
                  label="Start Date"
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  required
                />
                <TextField
                  fullWidth
                  label="End Date"
                  type="date"
                  value={formData.end_date}
                  onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  required
                />
              </Box>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button
              onClick={handleSubmit}
              variant="contained"
              disabled={!formData.loan_name || !formData.lender || !formData.original_amount || !formData.current_balance}
            >
              {selectedLoan ? 'Update' : 'Create'} Loan
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onClose={handleCloseDeleteDialog} maxWidth="sm" fullWidth>
          <DialogTitle>Delete Loan</DialogTitle>
          <DialogContent>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Are you sure you want to delete this loan? This action cannot be undone.
            </Typography>
            {selectedLoan && (
              <Box sx={{ p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                <Typography variant="subtitle2">Loan Details:</Typography>
                <Typography variant="body2">
                  <strong>Name:</strong> {selectedLoan.loan_name}
                </Typography>
                <Typography variant="body2">
                  <strong>Lender:</strong> {selectedLoan.lender}
                </Typography>
                <Typography variant="body2">
                  <strong>Type:</strong> {selectedLoan.loan_type}
                </Typography>
                <Typography variant="body2">
                  <strong>Current Balance:</strong> {formatCurrency(selectedLoan.current_balance)}
                </Typography>
                <Typography variant="body2">
                  <strong>Monthly Payment:</strong> {formatCurrency(selectedLoan.monthly_payment)}
                </Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDeleteDialog}>Cancel</Button>
            <Button onClick={handleDelete} variant="contained" color="error">
              Delete Loan
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Container>
  );
};